import { ButtonInline } from "@nova/component-btn"
import { ID, Permissions } from "@nova/constants"
import { MemberExtended, WorkspaceMember } from "@nova/schemas"
import { useTranslation } from "@nova/use-translation"
import { DiscriminatedUnion } from "@nova/util-ts"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import {
    inviteAllUsersToWorkspace,
    setActiveMember,
    updateMemberPermissionsHandler,
    updateWorkspaceMemberAccessRequest,
} from "features/entity-caching/members/UserManagementSlice"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback } from "react"

type AccessRequestButtonsProps = {
    entityId: ID
    member: DiscriminatedUnion<WorkspaceMember>
    onClose?: () => void
}
export const AccessRequestButtons = ({ entityId, member, onClose }: AccessRequestButtonsProps) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const activeWorkspaceId = useAppSelector(selectActiveWorkspaceId())
    const isRequestingWorkspaceAccess = entityId === activeWorkspaceId

    const removeAccessId = useCallback(() => {
        if (!activeWorkspaceId) return

        dispatch(
            updateWorkspaceMemberAccessRequest({
                workspaceId: activeWorkspaceId,
                userId: member.userId,
                accessRequestId: entityId,
                removeRequest: true,
            })
        )
        const permissions = member.permissions
        const isLastRequest =
            !permissions.workspaceAccess &&
            !permissions.subRoles &&
            permissions.accessRequestIds &&
            new Set(permissions.accessRequestIds).size <= 1
        if (isLastRequest && onClose) onClose()
    }, [activeWorkspaceId, member, dispatch, entityId, onClose])

    const handleApprove = useCallback(async () => {
        if (!activeWorkspaceId) return

        //either we approving an existing viewer to upgrade to editor, or we are approving new access as editor to an entity they didnt have before
        const isNewPermission =
            !member.permissions.subRoles?.find((role) => role.id === entityId) &&
            !member.permissions.workspaceAccess

        if (isNewPermission && member.email) {
            const members = await dispatch(
                inviteAllUsersToWorkspace({
                    workspaceId: activeWorkspaceId,
                    emailAddresses: [member.email],
                    permissions: isRequestingWorkspaceAccess
                        ? {
                              workspaceAccess: Permissions.Editor,
                              subRoles: [],
                              accessRequestIds: [],
                          }
                        : {
                              workspaceAccess: 0,
                              subRoles: [{ permission: Permissions.Editor, id: entityId }],
                              accessRequestIds: [],
                          },
                })
            ).unwrap()
            dispatch(setActiveMember(members[0] as MemberExtended))
            return
        }
        dispatch(
            updateMemberPermissionsHandler({
                workspaceId: activeWorkspaceId,
                member,
                accessLevel: isRequestingWorkspaceAccess ? "workspace" : "subRole",
                newPermission: Permissions.Editor,
                entityId,
                removeRequest: true,
            })
        )
    }, [isRequestingWorkspaceAccess, activeWorkspaceId, dispatch, entityId, member])

    return (
        <div className="flex items-center">
            <ButtonInline
                label="Deny"
                className="box-content font-semibold !pb-1.5 !m-0"
                onClick={removeAccessId}
            >
                {t("Deny")}
            </ButtonInline>
            <ButtonInline
                label="Approve"
                className="font-semibold box-content !border-solid !border !border-[#525252] !rounded-[4px] !px-2 !py-1"
                onClick={handleApprove}
            >
                {t("Approve")}
            </ButtonInline>
        </div>
    )
}
